import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { GetKYCWorkflowRowRes } from '@dealstack/shared/lib/generated/models/GetKYCWorkflowRowRes';
import { useNavigateToKycPage } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useNavigateToKycPage';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useKYCWorkflowsTable } from './useKYCWorkflowsTable';
import { createBulkActions, KycDownloadSelection } from './createBulkActions';
import { createQuickFilterOptions } from './utils.ts';
import {
	deleteMipStandaloneKycWorkflow,
	postMipKYCWorkflowsDownloadFiles,
} from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@mantine/core';
import { InvestmentWorkflowKycSettingsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/InvestmentWorkflowKycSettingsDrawer/InvestmentWorkflowKycSettingsDrawer.tsx';
import { InvestmentWorkflowInvestorDataDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/InvestmentWorkflowInvestorDataDrawer/InvestmentWorkflowInvestorDataDrawer.tsx';
import { TaxAndComplianceDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/components/TaxAndComplianceDrawer/TaxAndComplianceDrawer.tsx';

export const KYCWorkflowsTable = () => {
	const tMipManage = useTranslationMipManage();
	const { mipId } = useParams<{ mipId?: string }>();
	const { navigateToKycPage } = useNavigateToKycPage();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [selectedKycWorkflowId, setSelectedKycWorkflowId] = useState<string | undefined>(
		undefined
	);
	const [kycDrawerOpened, setKycDrawerOpened] = useState(false);
	const [investorDataDrawerOpened, setInvestorDataDrawerOpened] = useState(false);
	const [taxComplianceDrawerOpened, setTaxComplianceDrawerOpened] = useState(false);

	const handleOpenKycDrawer = useCallback((kycWorkflowId: string) => {
		setSelectedKycWorkflowId(kycWorkflowId);
		setKycDrawerOpened(true);
	}, []);

	const handleOpenInvestorDataDrawer = useCallback((kycWorkflowId: string) => {
		setSelectedKycWorkflowId(kycWorkflowId);
		setInvestorDataDrawerOpened(true);
	}, []);

	const handleOpenTaxComplianceDrawer = useCallback((kycWorkflowId: string) => {
		setSelectedKycWorkflowId(kycWorkflowId);
		setTaxComplianceDrawerOpened(true);
	}, []);

	const withdrawMutation = useMutation({
		mutationFn: deleteMipStandaloneKycWorkflow,
		onSuccess: () => {
			showToastV2({ title: tMipManage('kyc.manage.standalone_withdrawn') });
			void queryClient.invalidateQueries({ queryKey: [API_PATHS.MIP_KYC_WORKFLOWS_GET] });
		},
	});

	const onWithdrawStandalone = useCallback(
		(id: string) => withdrawMutation.mutate(id),
		[withdrawMutation.mutate]
	);

	const { columns, mantineTableDataOperations, workflows, isLoading, rowCount, downloadFilters } =
		useKYCWorkflowsTable({
			onWithdrawStandalone,
			onKycClick: handleOpenKycDrawer,
			onInvestorDataClick: handleOpenInvestorDataDrawer,
			onTaxComplianceClick: handleOpenTaxComplianceDrawer,
		});

	const { mutate: downloadFiles, isPending: isDownloading } = useMutation({
		mutationFn: (variables: Parameters<typeof postMipKYCWorkflowsDownloadFiles>[0]) =>
			postMipKYCWorkflowsDownloadFiles(variables),
		onSuccess: () => {
			showToastV2({
				title: tMipManage('modals.bulk-download.title'),
				state: 'success',
				message: (
					<div>
						<p>{tMipManage('modals.bulk-download.async-message')}</p>
						<Button
							variant="white"
							onClick={() => navigate('/exports', { replace: true })}
						>
							{tMipManage('modals.bulk-download.go-button')}
						</Button>
					</div>
				),
			});
		},
		onError: () => {
			showToastV2({ state: 'error', message: tMipManage('kyc.manage.download_error') });
		},
	});

	const onDownload = (selection: KycDownloadSelection) => {
		const hasExplicitSelection = selection.allSelected || selection.selectedIds.length > 0;
		const effectiveSelection: KycDownloadSelection = hasExplicitSelection
			? selection
			: { allSelected: true, selectedIds: [], excludedIds: [] };

		downloadFiles({ ...effectiveSelection, filters: downloadFilters });
	};

	return (
		<>
			<MantineTable<GetKYCWorkflowRowRes>
				getRowId={(row) => row.id}
				columns={columns}
				data={workflows}
				dataOperations={mantineTableDataOperations}
				isLoading={isLoading}
				rowCount={rowCount}
				renderBulkActionsMenu={true}
				actions={(table) =>
					createBulkActions(table, {
						isDownloading,
						onDownload,
						downloadLabel: tMipManage('kyc.download'),
					})
				}
				quickFilterOptions={createQuickFilterOptions(tMipManage)}
				enableSearchBar={true}
				enableFilters={true}
				enablePagination={true}
				enableColumnResizing={true}
				pinnedColumnIds={['actions', 'investor']}
				initialState={{ columnVisibility: { investorId: false } }}
				manualPagination={true}
				manualFiltering={true}
				manualSorting={true}
				enableRowSelection={true}
				enableRowActions={true}
				enableExpanding={true}
				enableHiding={true}
				enableTopToolbar={true}
				enableStickyHeader={true}
				enableStickyFooter={true}
				hideFooterRows={false}
				enableVerticalCellBorders={false}
				footerCellMinHeight="48px"
				footerCellAlignTop={true}
				mantineTableBodyRowProps={({ row }) => ({
					onClick: () =>
						navigateToKycPage({
							investorId: row.original.investorId,
							kycWorkflowId: row.original.id,
						}),
					style: {
						cursor: 'pointer',
						height: 'unset',
						placeItems: row.getIsExpanded() ? 'flex-start' : 'center',
					},
				})}
			/>
			<InvestmentWorkflowKycSettingsDrawer
				mipId={mipId ?? ''}
				opened={kycDrawerOpened}
				onClose={() => setKycDrawerOpened(false)}
				kycWorkflowId={selectedKycWorkflowId}
			/>
			<InvestmentWorkflowInvestorDataDrawer
				opened={investorDataDrawerOpened}
				onClose={() => setInvestorDataDrawerOpened(false)}
				kycWorkflowId={selectedKycWorkflowId}
			/>
			<TaxAndComplianceDrawer
				mipId={mipId ?? ''}
				opened={taxComplianceDrawerOpened}
				onClose={() => setTaxComplianceDrawerOpened(false)}
				kycWorkflowId={selectedKycWorkflowId}
			/>
		</>
	);
};
